import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { setCurrentUser } from "../../stores/current-user";
import { type MimicState, mimicState, setMimicState } from "../../stores/mimic";
import type { BrandColor, OrganizationListItem, User } from "../../types";
import { BrandedHeader, type BrandedHeaderTab } from "./branded-header";
import { ContentCard, ContentCardBody } from "./content-card";
import { ErrorDialog } from "./error-dialog";
import { MimicBanner } from "./mimic-banner";

type BrandedLayoutProps = {
  children: ComponentChildren;
  /** Current user */
  user: User;
  /** Mimic state (for admin impersonation) */
  mimic?: MimicState;
  /** Current workspace ID */
  orgId: string;
  /** Current workspace name */
  orgName: string;
  /** Workspace brand color */
  brandColor: BrandColor;
  /** Active navigation tab */
  activeTab: BrandedHeaderTab;
  /** Whether user is admin of this workspace */
  isAdmin: boolean;
  /** Organizations for workspace switcher */
  organizations?: OrganizationListItem[];
  /** Form count for tab badge */
  formCount?: number;
  /** Whether the current user has direct reports (controls Team tab visibility) */
  hasReports?: boolean;
  /** Optional sidebar content (for two-column layout) */
  sidebar?: ComponentChildren;
};

/**
 * Full branded page layout with header, content card, and optional sidebar.
 * Use this for all workspace pages for consistent branding.
 */
export function BrandedLayout({
  children,
  user,
  mimic,
  orgId,
  orgName,
  brandColor,
  activeTab,
  isAdmin,
  organizations,
  formCount,
  hasReports,
  sidebar,
}: BrandedLayoutProps) {
  // Initialize the global currentUser and mimic signals from the page's props
  useEffect(() => {
    setCurrentUser(user);
    setMimicState(mimic ?? null);
  }, [user, mimic]);

  const isMimicking = mimicState.value !== null;

  return (
    <div class={`min-h-screen bg-surface-page ${isMimicking ? "pt-10" : ""}`}>
      <MimicBanner />
      <ErrorDialog />

      <div data-brand={brandColor}>
        <BrandedHeader
          orgId={orgId}
          orgName={orgName}
          brandColor={brandColor}
          activeTab={activeTab}
          isAdmin={isAdmin}
          organizations={organizations}
          formCount={formCount}
          hasReports={hasReports}
        />

        <div class="px-4 lg:px-8 pb-8">
          <ContentCard>
            <ContentCardBody>
              {sidebar ? (
                <div class="flex flex-col lg:flex-row gap-6 lg:gap-8">
                  {/* Sidebar - shows first on mobile, left on desktop */}
                  <div class="lg:w-72 flex-shrink-0 order-first lg:order-none">
                    {sidebar}
                  </div>
                  {/* Main content */}
                  <div class="flex-1 min-w-0">{children}</div>
                </div>
              ) : (
                children
              )}
            </ContentCardBody>
          </ContentCard>
        </div>
      </div>
    </div>
  );
}
